import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import type { ArticleDetailDto } from '@zoxo/shared';

import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/** Query key for a single article detail. Namespaced by auth state. */
export function articleDetailKey(id: string, authed: boolean) {
  return ['article', id, authed ? 'auth' : 'anon'] as const;
}

/**
 * Article detail (GET /articles/:id, V4 §C). Suspense read — wrapped in an Error
 * Boundary at the route so a missing / unapproved-and-not-owned article (404)
 * shows a fallback. An unapproved (isActive=false) article is
 * only reachable by its author / admin; everyone else gets a 404 from the server.
 *
 * The key is namespaced by auth state so signing in/out refetches with the
 * correct viewer context (isMine, and pending visibility for the author).
 */
export function articleQuery(id: string, authed: boolean) {
  return queryOptions({
    queryKey: articleDetailKey(id, authed),
    queryFn: () => api.get<ArticleDetailDto>(`/articles/${id}`),
  });
}

export function useArticle(id: string) {
  const { isAuthenticated } = useAuth();
  return useSuspenseQuery(articleQuery(id, isAuthenticated));
}
